import { CheckIcon } from "@heroicons/react/24/outline";
import { Command as CommandPrimitive } from "cmdk";
import { useState, useRef, useCallback, type KeyboardEvent } from "react";
import React from "react";
import {
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/core/components/ui/command";
import { useDialogPortalContainer } from "@/core/components/ui/dialog";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import { focusNextTabbable } from "./focus-next-tabbable";

type Props<T extends string> = {
  id?: string;
  ariaLabel?: string;
  selectedValue: T;
  onSelectedValueChange: (value: T) => void;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  items: { value: T; label: string }[];
  renderOption?: (option: { value: T; label: string }) => React.ReactNode;
  isLoading?: boolean;
  emptyMessage?: string;
  placeholder?: string;
  label?: string;
  /** Same contract as Input: styles the field as invalid. The caller renders the message. */
  error?: string;
  fullWidth?: boolean;
  required?: boolean;
  disableRequirementAsterisk?: boolean;
  persistentLabel?: boolean;
  className?: string;
  onAddNew?: () => void;
  addNewLabel?: string;
  scrollAreaClassName?: string;
  containScroll?: boolean;
  disabled?: boolean;
  disabledTooltip?: React.ReactNode | string;
};

export const AutoComplete = React.forwardRef<HTMLInputElement, Props<string>>(
  (
    {
      id,
      ariaLabel,
      selectedValue,
      onSelectedValueChange,
      searchValue,
      onSearchValueChange,
      items,
      renderOption,
      isLoading,
      emptyMessage = "No items.",
      placeholder = "Search...",
      label,
      error,
      fullWidth,
      required,
      disableRequirementAsterisk,
      persistentLabel,
      className,
      onAddNew,
      scrollAreaClassName,
      containScroll,
      addNewLabel,
      disabled,
      disabledTooltip,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const [isOpen, setOpen] = useState(false);
    const [hasFocused, setHasFocused] = useState(false);
    const [highlightedValue, setHighlightedValue] = useState("");
    const [isClickingAddNew, setIsClickingAddNew] = useState(false);
    const isSelectedValueInactive = Boolean(selectedValue && !hasFocused);

    // Check if we're inside a dialog
    const dialogPortalContainer = useDialogPortalContainer();

    // Combine the forwarded ref with our internal ref
    React.useImperativeHandle(ref, () => inputRef.current!, []);

    React.useEffect(() => {
      const selectedItem = items.find((item) => item.value === selectedValue);
      if (selectedItem && !searchValue && !hasFocused) {
        onSearchValueChange(selectedItem.label);
      }
    }, [selectedValue, items, searchValue, onSearchValueChange, hasFocused]);

    // A new search can keep the same highlight, so cmdk may not scroll it.
    React.useLayoutEffect(() => {
      if (isOpen && listRef.current) listRef.current.scrollTop = 0;
    }, [isOpen, searchValue]);

    // Filtering can scroll the previous highlight before selecting the first item.
    React.useLayoutEffect(() => {
      const list = listRef.current;
      if (!isOpen || !list) return;

      const firstItem = list.querySelector("[cmdk-item]");
      if (firstItem?.getAttribute("data-value") === highlightedValue) {
        list.scrollTop = 0;
      }
    }, [isOpen, highlightedValue, searchValue]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        const input = inputRef.current;
        if (!input) return;

        if (event.key === "Tab") return;

        if (!isOpen) {
          setOpen(true);
        }

        if (event.key === "Enter" && input.value !== "") {
          const selectedItem =
            items.find((item) => item.value === highlightedValue) ??
            items.find((item) => item.label === input.value);
          if (selectedItem) {
            onSelectedValueChange(selectedItem.value);
            onSearchValueChange(selectedItem.label);
            setOpen(false);
            setHasFocused(false);
          }
        }

        if (event.key === "Escape") {
          input.blur();
        }
      },
      [
        highlightedValue,
        isOpen,
        items,
        onSelectedValueChange,
        onSearchValueChange,
      ],
    );

    const handleInputKeyDownCapture = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Tab") {
          event.preventDefault();
          event.stopPropagation();
          focusNextTabbable(event.currentTarget, event.shiftKey);
        }
      },
      [],
    );

    const handleFocus = useCallback(() => {
      if (disabled) return;
      setOpen(true);
      setHasFocused(true);
      onSearchValueChange("");
    }, [onSearchValueChange, disabled]);

    const handleInputClick = useCallback(() => {
      if (disabled || hasFocused) return;
      setOpen(true);
      setHasFocused(true);
      onSearchValueChange("");
    }, [disabled, hasFocused, onSearchValueChange]);

    const handleBlur = useCallback(() => {
      // Don't close if we're clicking the Add New button
      if (isClickingAddNew) {
        setIsClickingAddNew(false);
        return;
      }

      setOpen(false);
      setHasFocused(false);
      const selectedItem = items.find((item) => item.value === selectedValue);
      if (selectedItem) {
        onSearchValueChange(selectedItem.label);
      } else {
        // Clear input text if no value is selected
        onSearchValueChange("");
      }
    }, [selectedValue, items, onSearchValueChange, isClickingAddNew]);

    const handleSelectOption = useCallback(
      (value: string) => {
        const selectedItem = items.find((item) => item.value === value);
        if (selectedItem?.value === selectedValue) {
          // remove the selected item
          onSelectedValueChange("" as string);
          onSearchValueChange("");
          setOpen(false);
          setHasFocused(false);
        } else if (selectedItem) {
          onSelectedValueChange(selectedItem.value);
          onSearchValueChange(selectedItem.label);
          setOpen(false);
          setHasFocused(false);
        }
      },
      [items, onSelectedValueChange, onSearchValueChange, selectedValue],
    );

    // Add asterisk to placeholder if required
    const placeholderWithAsterisk = required ? `${placeholder} *` : placeholder;

    const inputElement = (
      <div className={cn("relative p-px", { "w-full": fullWidth })}>
        <CommandPrimitive
          onKeyDown={handleKeyDown}
          value={highlightedValue}
          onValueChange={setHighlightedValue}
          onMouseLeave={() => setHighlightedValue("")}
        >
          <div>
            <CommandPrimitive.Input
              id={id}
              aria-label={ariaLabel}
              ref={inputRef}
              value={searchValue}
              onValueChange={onSearchValueChange}
              onBlur={handleBlur}
              autoFocus={false}
              onFocus={handleFocus}
              onClick={handleInputClick}
              onKeyDownCapture={handleInputKeyDownCapture}
              placeholder={placeholderWithAsterisk}
              className={cn(
                "flex h-14 w-full rounded-lg border border-input bg-card px-4 text-lg shadow-sm transition-colors",
                {
                  "pb-2 pt-6": Boolean(searchValue && label),
                  "caret-transparent": isSelectedValueInactive,
                  "border-destructive focus-visible:ring-destructive/50":
                    Boolean(error),
                },
                "placeholder:text-lg placeholder:text-tertiary-text",
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                "disabled:cursor-not-allowed disabled:border-stroke-tertiary disabled:bg-bg-secondary disabled:text-tertiary-text",
                className,
              )}
              aria-invalid={error ? true : undefined}
              disabled={disabled}
            />
            {label && (persistentLabel || searchValue) && (
              <Typography
                variant="subtitleW3"
                className={cn("absolute left-4 top-2 text-tertiary-text", {
                  "text-destructive": Boolean(error),
                })}
              >
                {label}
                {required && !disableRequirementAsterisk && (
                  <span className="ml-1">*</span>
                )}
              </Typography>
            )}
          </div>
          <div className="relative mt-1">
            <div
              className={cn(
                "absolute top-0 w-full rounded-xl bg-white shadow-[0_4px_4px_0_rgba(0,0,0,0.25)] outline-none",
                {
                  "z-[9999]": Boolean(dialogPortalContainer),
                  "z-50": !dialogPortalContainer,
                  block: isOpen && !disabled,
                  hidden: !isOpen || disabled,
                },
              )}
            >
              <div className="rounded-lg ring-1 ring-slate-200">
                <CommandList
                  ref={listRef}
                  className={cn(
                    "scrollbar-hidden max-h-[170px] overflow-y-auto",
                    { "overscroll-contain": containScroll },
                    scrollAreaClassName,
                  )}
                  onWheel={
                    containScroll
                      ? (event) => event.stopPropagation()
                      : undefined
                  }
                >
                  {isLoading ? (
                    <CommandPrimitive.Loading>
                      <Stack className="p-1">
                        <Skeleton className="h-8 w-full" />
                      </Stack>
                    </CommandPrimitive.Loading>
                  ) : null}
                  {items.length > 0 && !isLoading ? (
                    <CommandGroup>
                      {items.map((option) => (
                        <CommandItem
                          key={option.value}
                          value={option.value}
                          keywords={[option.label]}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onSelect={() => handleSelectOption(option.value)}
                          className="flex w-full items-center gap-2"
                        >
                          <CheckIcon
                            className={cn("mr-2 h-4 w-4", {
                              "opacity-100": selectedValue === option.value,
                              "opacity-0": selectedValue !== option.value,
                            })}
                          />
                          {renderOption ? renderOption(option) : option.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ) : null}
                  {!isLoading && items.length === 0 ? (
                    <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm">
                      {emptyMessage}
                    </CommandPrimitive.Empty>
                  ) : null}
                </CommandList>
                {onAddNew && (
                  <div
                    className="relative flex cursor-pointer items-center rounded-b-xl bg-white py-2 pl-6 pr-4 hover:bg-accent"
                    onMouseEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setHighlightedValue("");
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsClickingAddNew(true);
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onAddNew();
                      inputRef.current?.blur();
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <Typography variant="subtitleW3" textColor="interactive">
                      {addNewLabel || `+ New ${label || "item"}`}
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CommandPrimitive>
      </div>
    );

    if (disabled && disabledTooltip) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{inputElement}</TooltipTrigger>
          <TooltipContent>{disabledTooltip}</TooltipContent>
        </Tooltip>
      );
    }

    return inputElement;
  },
);
